import { __extends } from "tslib";
import React from 'react';
import classnames from 'classnames';
import { compose } from 'redux';
import { withTranslation } from 'react-i18next';
import { Url } from '@wix/da-url';
import ddt from '@wix/da-ddt';
import { withLogBiEvent, } from '../../../biLogger/hocs/withLogBiEvent';
import { BiEventType, BiDataHook, ReferralType } from '../../../biLogger';
import CommentBox from '../../../CommentBox';
import SquareHeartFilled from '../../../Icons/SquareHeartFilled';
import SquareHeartOutlined from '../../../Icons/SquareHeartOutlined';
import Timestamp from '../../../Timestamp';
import { dateDiff } from '../../../Timestamp/functions';
import shortenNumber from '../../../utils/shortenNumber';
import { generateReplyEditorId } from '../../utils';
import { IconSize } from '../../../Icons/IconWrap';
import { withMobileContext } from '../../../Context';
import { withVerification, } from '../../../VerificationPopup/withVerification';
import LoadableCommentReduxModule from '../../LoadableCommentReduxModule';
import { withCommentContext, } from '../CommentContext/CommentContext';
import VisuallyHidden from '../../../a11y/VisuallyHidden';
import PlainButtonOrLink from '../../../Button/PlainButtonOrLink';
import s from './CommentFooter.scss';
var CommentFooter = /** @class */ (function (_super) {
    __extends(CommentFooter, _super);
    function CommentFooter() {
        var _this = _super !== null && _super.apply(this, arguments) || this;
        _this.onClickLike = function () {
            var _a = _this.props, comment = _a.comment, onUnlike = _a.onUnlike, onLike = _a.onLike;
            // don't allow liking your own comment
            if (comment.isAuthor) {
                return;
            }
            if (comment.isLiked) {
                onUnlike && onUnlike(comment);
            }
            else {
                onLike && onLike(comment);
            }
        };
        _this.onClickEdit = function () {
            var _a = _this.props, isVerificationNeeded = _a.isVerificationNeeded, pushVerificationPopup = _a.pushVerificationPopup, setEditingState = _a.setEditingState;
            if (isVerificationNeeded) {
                pushVerificationPopup('comment');
                return;
            }
            setEditingState('editing');
        };
        _this.onCloseEditor = function () {
            var setEditingState = _this.props.setEditingState;
            setEditingState('none');
        };
        _this.onClickReply = function () {
            var _a;
            var _b = _this.props, isVerificationNeeded = _b.isVerificationNeeded, pushVerificationPopup = _b.pushVerificationPopup, isReplying = _b.isReplying, setEditingState = _b.setEditingState, comment = _b.comment, logBiEvent = _b.logBiEvent, focusEditor = _b.focusEditor, isLoggedIn = _b.isLoggedIn, restrictLoggedOut = _b.restrictLoggedOut;
            if (isVerificationNeeded) {
                pushVerificationPopup('comment');
                return;
            }
            if (!isLoggedIn) {
                restrictLoggedOut({ restrictedActionName: ReferralType.COMMENT });
                return;
            }
            if (!isReplying) {
                logBiEvent({
                    evid: BiEventType.CLICK_REPLY_TO_COMMENT,
                    commenterid: (_a = comment.user) === null || _a === void 0 ? void 0 : _a.useridUuid,
                    commentid: comment.commentId,
                    typeid: comment.typeId,
                    itemid: comment.itemId,
                });
            }
            ddt.log('comments', 'Replying on comment, editorId: ', generateReplyEditorId(comment));
            setEditingState(isReplying ? 'none' : 'replying');
            focusEditor(generateReplyEditorId(comment));
        };
        _this.onReplyDone = function (result) {
            var _a = _this.props, setEditingState = _a.setEditingState, onReplyPosted = _a.onReplyPosted;
            setEditingState('none');
            onReplyPosted && result.comment && onReplyPosted(result);
        };
        return _this;
    }
    CommentFooter.prototype.render = function () {
        var _a = this.props, isReplying = _a.isReplying, isCommentVisible = _a.isCommentVisible, className = _a.className;
        if (!isCommentVisible) {
            return null;
        }
        return (React.createElement(React.Fragment, null,
            React.createElement(LoadableCommentReduxModule, null),
            React.createElement("div", { className: classnames(s['footer'], className), "data-hook": BiDataHook.COMMENT_FOOTER },
                this.renderEditReplyDelete(),
                this.renderLikeButton(),
                this.renderTime()),
            isReplying && this.canReply() && this.renderReply()));
    };
    CommentFooter.prototype.renderLikeButton = function () {
        var _a;
        var _b = this.props, t = _b.t, comment = _b.comment, likeButtonClassName = _b.likeButtonClassName, isCommentReduxReady = _b.isCommentReduxReady;
        var isAuthor = comment.isAuthor, isLiked = comment.isLiked, likes = comment.likes;
        var canLike = !isAuthor;
        var HeartIcon = isLiked ? SquareHeartFilled : SquareHeartOutlined;
        var heartClasses = classnames(isLiked ? s['liked'] : s['like'], isAuthor && s['not-clickable']);
        return (React.createElement("div", { className: classnames(s['footer-item'], s['action']) },
            React.createElement(PlainButtonOrLink, { className: classnames(s['likebutton'], likeButtonClassName), onClick: this.onClickLike, disabled: !isCommentReduxReady, "data-hook": BiDataHook.COMMENT_LIKE_BUTTON, biData: canLike
                    ? {
                        evid: BiEventType.CLICK_COMMENT_LIKE,
                        commenterid: (_a = comment.user) === null || _a === void 0 ? void 0 : _a.useridUuid,
                        commentid: comment.commentId,
                        typeid: comment.typeId,
                        itemid: comment.itemId,
                        is_liking: isLiked ? undefined : 1,
                    }
                    : undefined },
                React.createElement(HeartIcon, { size: IconSize.MEDIUM, className: heartClasses, disableDefaultColors: true }),
                likes > 0 && (React.createElement("span", { className: s['likes'] },
                    shortenNumber(likes),
                    React.createElement(VisuallyHidden, null, t('comment.action.likes', { count: likes })))))));
    };
    CommentFooter.prototype.renderReply = function () {
        var _a = this.props, comment = _a.comment, currentUser = _a.currentUser, canPostComment = _a.canPostComment, replyClassName = _a.replyClassName;
        return (React.createElement("div", { className: classnames(s['reply-block'], replyClassName) },
            React.createElement(CommentBox, { onPublishSuccess: this.onReplyDone, id: generateReplyEditorId(comment), className: s['commentbox-reply'], commentableType: comment.typeId, commentableItemId: comment.itemId, user: currentUser, replyComment: comment, autoFocus: true, onClose: this.onCloseEditor, canPostComment: !!canPostComment, compact: true })));
    };
    CommentFooter.prototype.renderTime = function () {
        var _a = this.props, t = _a.t, isMobile = _a.isMobile, comment = _a.comment, timestampClassName = _a.timestampClassName;
        var isEdited = !!comment.edited;
        var timestamp = isEdited ? comment.edited : comment.posted;
        var showEditedPrefix = isEdited && !isMobile;
        return (React.createElement("a", { href: Url.commentLink(comment.typeId, comment.itemId, comment.commentId), className: classnames(s['footer-item'], s['timestamp'], timestampClassName) },
            showEditedPrefix && t('comment.info.edited') + " ",
            React.createElement(Timestamp, { timestamp: timestamp, showTooltip: true, isCapitalized: !showEditedPrefix })));
    };
    CommentFooter.prototype.renderEditReplyDelete = function () {
        var _a = this.props, t = _a.t, canPostComment = _a.canPostComment, isLoggedIn = _a.isLoggedIn, comment = _a.comment, isCommentReduxReady = _a.isCommentReduxReady;
        if (!canPostComment && !!isLoggedIn) {
            return null;
        }
        return (React.createElement(React.Fragment, null,
            this.canEdit() && (React.createElement(PlainButtonOrLink, { className: classnames(s['footer-item'], s['action']), onClick: this.onClickEdit, "data-hook": BiDataHook.COMMENT_EDIT_BUTTON, biData: {
                    evid: BiEventType.COMMENT_EDIT_CLICK,
                    commentid: comment.commentId,
                    typeid: comment.typeId,
                    itemid: comment.itemId,
                }, disabled: !isCommentReduxReady }, t('comment.action.edit'))),
            React.createElement(PlainButtonOrLink, { className: classnames(s['footer-item'], s['action']), onClick: this.onClickReply, "data-hook": BiDataHook.COMMENT_REPLY_BUTTON, disabled: !isCommentReduxReady }, t('comment.action.reply'))));
    };
    CommentFooter.prototype.canReply = function () {
        var comment = this.props.comment;
        return !comment.isHidden && !comment.isSpam;
    };
    CommentFooter.prototype.canEdit = function () {
        // Limited to 1 day since post
        var _a = this.props, disableEdit = _a.disableEdit, comment = _a.comment;
        return (!disableEdit &&
            comment.isAuthor &&
            dateDiff(new Date().toISOString(), comment.posted, 'days') === 0);
    };
    CommentFooter.defaultProps = {
        canPostComment: true,
    };
    return CommentFooter;
}(React.Component));
var enhance = compose(withLogBiEvent, withCommentContext, withVerification, withMobileContext, withTranslation());
export default enhance(CommentFooter);
//# sourceMappingURL=CommentFooter.js.map